import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map, of, timeout } from 'rxjs';
import { loadDashboardPage, loadPortalShell } from '../../app.loaders';
import { AUTH_DESTINATION_ROUTE, AuthDestination, isPortalUrl } from './auth-destination';
import { AuthSessionService } from './auth-session.service';

/**
 * Builds a guard that admits only the given destinations, redirecting everything else to its
 * matching route. A stalled auth check (e.g. a hung network) falls back to `login` after 20s
 * rather than leaving the router pending forever.
 */
export function authDestinationGuard(allowed: readonly AuthDestination[]): CanActivateFn {
  return (_route, state) => {
    const session = inject(AuthSessionService);
    const router = inject(Router);

    return session.awaitDestination().pipe(
      timeout({ first: 20_000, with: () => of<AuthDestination>('login') }),
      map((destination) => {
        if (allowed.includes(destination)) {
          return true;
        }
        // Only portal deep links carry the visitor back where they came from — auth's own screens
        // (e.g. link-account, permission-revoked) never bounced through /login in the first place.
        const extras =
          destination === 'login' && isPortalUrl(state.url) ? { queryParams: { returnUrl: state.url } } : undefined;
        return router.createUrlTree([AUTH_DESTINATION_ROUTE[destination]], extras);
      })
    );
  };
}

const portalGuard = authDestinationGuard(['portal']);

/**
 * The portal shell's guard. It waits for Firebase plus the login GET, and only then would the router
 * start loading the shell and the page — so it starts those chunk downloads itself, alongside the
 * wait. The dashboard only when it is the target, a deep link does not need it. A failed download
 * is left to the router's own load, which reports it.
 */
export const authGuard: CanActivateFn = (route, state) => {
  void loadPortalShell().catch(() => undefined);
  if (route.firstChild?.routeConfig?.loadComponent === loadDashboardPage) {
    void loadDashboardPage().catch(() => undefined);
  }
  return portalGuard(route, state);
};

export const guestGuard = authDestinationGuard(['login']);
export const linkAccountGuard = authDestinationGuard(['link-account']);
export const permissionRevokedGuard = authDestinationGuard(['permission-revoked']);
/**
 * `activate/done` (BRO-1171 C1): once `activate` succeeds and the session reloads, the person
 * GET can already return 200 by the time this guard runs — the destination is `portal`, which a
 * plain `guestGuard` would bounce straight past the done screen. `login` stays admitted too, for
 * the window between redeeming the code and the reload actually landing (registration flow still
 * active, no fresh person yet).
 */
export const registrationDoneGuard = authDestinationGuard(['portal', 'login']);

/**
 * Whether the visitor is a signed-in portal user — a `canMatch`, not a `canActivate`, because
 * the answer picks a route rather than blocking one.
 *
 * The Jahreszahlen link (BRO-1177), the document-upload link (BRO-1188), the onboarding
 * invitation (BRO-1178) and the sign link (BRO-1179) are opened by whoever got the mail, signed in
 * or not, so `annual-figures`, `document-upload`, `onboarding` and `sign` are declared twice:
 * once inside the portal shell with this guard, once standalone after it. A false decision here
 * does not redirect — the router backtracks and matches the standalone route instead, which is
 * exactly the page without the portal chrome.
 *
 * Same 20s stall fallback as `authDestinationGuard`: a hung auth check must not leave a public
 * link pending forever, and "not a portal user" is the shell-less page, which always works.
 */
export const sessionMatch: CanMatchFn = () => {
  const session = inject(AuthSessionService);

  return session.awaitDestination().pipe(
    timeout({ first: 20_000, with: () => of<AuthDestination>('login') }),
    map((destination) => destination === 'portal')
  );
};
